import { useEffect, useMemo, useState } from 'react';
import { WalletReadyState } from '@solana/wallet-adapter-base';
import { useWalletStore } from '@/stores/wallet';

export interface WalletPickerProps {
  onClose: () => void;
}

export default function WalletPicker({ onClose }: WalletPickerProps) {
  const available = useWalletStore((s) => s.available);
  const connect = useWalletStore((s) => s.connect);
  const initDiscovery = useWalletStore((s) => s.initDiscovery);

  const [error, setError] = useState<string | null>(null);
  const [busyName, setBusyName] = useState<string | null>(null);

  useEffect(() => {
    initDiscovery();
  }, [initDiscovery]);

  const sortedWallets = useMemo(() => {
    const order = (state: WalletReadyState) => {
      if (state === WalletReadyState.Installed) return 0;
      if (state === WalletReadyState.Loadable) return 1;
      return 2;
    };
    return [...available].sort((a, b) => order(a.readyState) - order(b.readyState));
  }, [available]);

  function isInstalled(state: WalletReadyState) {
    return state === WalletReadyState.Installed || state === WalletReadyState.Loadable;
  }

  async function pick(name: string) {
    if (busyName) return;
    setBusyName(name);
    setError(null);
    try {
      await connect(name);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyName(null);
    }
  }

  function backdrop(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm"
      onClick={backdrop}
    >
      <div className="w-[min(420px,calc(100vw-2rem))] rounded-xl border border-ink-700 bg-ink-900 shadow-2xl">
        <header className="flex items-center justify-between px-5 py-4 border-b border-ink-700">
          <h3 className="text-base font-semibold text-ink-50">Connect a wallet</h3>
          <button
            type="button"
            className="text-ink-400 hover:text-ink-50 transition-colors"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>

        <div className="p-3">
          <ul className="flex flex-col gap-1">
            {sortedWallets.map((w) => (
              <li key={w.name}>
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-ink-800 transition-colors disabled:opacity-50"
                  disabled={!!busyName}
                  onClick={() => pick(w.name)}
                >
                  <img src={w.icon} alt={w.name} className="w-7 h-7 rounded-md" />
                  <span className="text-sm font-medium text-ink-50 flex-1 text-left">{w.name}</span>
                  {busyName === w.name ? (
                    <span className="text-xs text-ink-400">Connecting…</span>
                  ) : isInstalled(w.readyState) ? (
                    <span className="text-xs text-ink-500">→</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider text-ink-500">Install</span>
                  )}
                </button>
              </li>
            ))}
          </ul>

          {error && <p className="mt-3 px-3 text-xs text-red-400">{error}</p>}
        </div>

        <footer className="px-5 py-3 border-t border-ink-700 text-[11px] text-ink-500">
          Phantom, Solflare, Backpack supported.
        </footer>
      </div>
    </div>
  );
}
